import { useCallback, useEffect, useState } from "react"
import { DOMAIN_API_HOST } from "../../../../../configs/main"
import { useSnackbar } from "notistack";
import { Switch, Dialog, DialogTitle, DialogContent, IconButton, Box } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import { Banners, getAdEditLockState, getDefaultUtmSource } from "./Banners";
import useUser from "../../../../../data/useUser";
import { trackWizardEvent } from "../../../../../utils/productEvents";

const REJECT_REASONS = [
  "Отсутствие на баннере ИНН и наименования юридического лица",
  "Загрузка разных креативов в рамках одного объявления",
  "Запрещенная в соответствии с законом “О рекламе” тематика",
  "Запрещенные, в соответствии с законом “О рекламе” слова и выражения",
  "Отсутствие дисклеймера (мед услуги)",
  "Несоответствие ИНН на баннере реквизитам юридического лица",
  "Указанная посадочная страница не открывается",
  "Недопустимые ресайзы баннеров",
];

function isFeedRejected(feed) {
  return feed?.yml_feed_moderate_status === "REJECTED";
}

function existingErid(ad) {
  return String(ad?.erid || "").trim();
}

function AdsManagersLoading({ label }) {
  return (
    <div className="ads-mgr-loading" role="status" aria-live="polite" aria-busy="true">
      <span className="ads-mgr-spinner" aria-hidden="true" />
      <span>{label}</span>
    </div>
  );
}

function getFeedStatusMeta(feed) {
  const status = feed?.yml_feed_moderate_status;
  if (status === "REJECTED") {
    return { label: "Отклонён", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" };
  }
  if (status === "ACCEPTED") {
    return { label: "Одобрен", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" };
  }
  if (status === "MODERATING") {
    return { label: "На модерации", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa" };
  }
  return { label: "Прайс-лист", color: "#52525b", bg: "#f4f4f5", border: "#e4e4e7" };
}

function formatPxSize(width, height) {
  const w = Number(width);
  const h = Number(height);
  if (!w || !h) return "";
  return `${w}×${h}`;
}

function CopyIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
      <path d="m21 3-9 9" />
      <path d="M15 3h6v6" />
    </svg>
  );
}

function FeedUrlRow({ label, url, onCopied }) {
  if (!url) return null;
  return (
    <div className="ads-mgr-urlrow">
      <div style={{ minWidth: 0 }}>
        <div className="ads-mgr-urlrow__label">{label}</div>
        <div className="ads-mgr-urlrow__value" title={url}>{url}</div>
      </div>
      <div className="ads-mgr-urlrow__actions">
        <button
          type="button"
          className="ads-mgr-iconbtn"
          aria-label={`Скопировать: ${label}`}
          onClick={() => {
            navigator.clipboard.writeText(url);
            if (onCopied) onCopied();
          }}
        >
          <CopyIcon />
        </button>
        <a
          className="ads-mgr-iconbtn"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Открыть ${label} в браузере`}
        >
          <ExternalIcon />
        </a>
      </div>
    </div>
  );
}

function ManagerBannerThumb({ banner, alt }) {
  const [size, setSize] = useState("");
  const isVideo = banner.extension && banner.extension.toLowerCase() === "mp4";
  const applySize = (width, height) => {
    const label = formatPxSize(width, height);
    if (label) setSize(label);
  };

  return (
    <div className="ads-mgr-banner">
      {isVideo ? (
        <video
          src={banner.url}
          referrerPolicy="unsafe-url"
          muted
          controls
          onLoadedMetadata={(e) => applySize(e.currentTarget.videoWidth, e.currentTarget.videoHeight)}
        />
      ) : (
        <img
          src={banner.url}
          referrerPolicy="unsafe-url"
          alt={alt}
          onLoad={(e) => applySize(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
        />
      )}
      <a
        href={banner.url}
        target="_blank"
        rel="noopener noreferrer"
        className="ads-mgr-banner__open"
        aria-label={size ? `Открыть оригинал баннера ${size}` : "Открыть оригинал баннера"}
      >
        <ExternalIcon />
      </a>
      {size ? <span className="ads-mgr-banner__size">{size}</span> : null}
    </div>
  );
}

function FeedRejectNotice({ feed, isMobile = false }) {
  if (!isFeedRejected(feed)) return null;
  return (
    <div
      style={{
        marginTop: 8,
        padding: "8px 10px",
        borderRadius: 6,
        background: "#fff7ed",
        border: "1px solid #fed7aa",
      }}
    >
      <span style={{ fontSize: isMobile ? 11 : 12, color: "#9a3412", lineHeight: 1.45, fontWeight: 600 }}>
        Прайс‑лист отклонён
      </span>
    </div>
  );
}

const PREVIEW_SLOT_COUNT = 3;

function getAdStatusMeta(ad) {
  const moderateStatus = ad.moderate_status;

  if (moderateStatus === "REJECTED") {
    return { label: "Отклонено", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" };
  }
  if (ad.status === "ACTIVE" && moderateStatus === "MODERATING") {
    return { label: "На модерации", color: "#ea580c", bg: "#fff7ed", border: "#fed7aa" };
  }
  if (ad.status === "ACTIVE" && moderateStatus === "ACCEPTED") {
    return { label: "Активно", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" };
  }
  return { label: "Выключено", color: "#52525b", bg: "#f4f4f5", border: "#e4e4e7" };
}

const AdListItem = ({ ad, isMobile, onEdit, onToggleStatus }) => {
  const status = getAdStatusMeta(ad);
  const previewBanners = [...(ad.banners || [])].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  ).slice(0, PREVIEW_SLOT_COUNT);
  const previewSlots = Array.from(
    { length: PREVIEW_SLOT_COUNT },
    (_, index) => previewBanners[index] || null
  );
  const thumbSize = isMobile ? 56 : 64;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        gap: isMobile ? 12 : 16,
        padding: isMobile ? "14px" : "16px 20px",
        borderRadius: 10,
        background: "#fff",
        border: `1px solid ${status.border}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        transition: "box-shadow 0.15s ease",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 8,
          flexShrink: 0,
          width: thumbSize * PREVIEW_SLOT_COUNT + 8 * (PREVIEW_SLOT_COUNT - 1),
        }}
      >
        {previewSlots.map((banner, index) => (
          <div
            key={banner?.id || `empty-${index}`}
            style={{
              width: thumbSize,
              height: thumbSize,
              borderRadius: 8,
              overflow: "hidden",
              background: banner ? "#f0f1f3" : "#fafafa",
              border: banner ? "none" : "1px dashed #e4e4e7",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {banner ? (
              banner.extension?.toLowerCase() === "mp4" ? (
                <video
                  src={banner.url}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  muted
                  referrerPolicy="unsafe-url"
                />
              ) : (
                <img
                  src={banner.url}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  referrerPolicy="unsafe-url"
                />
              )
            ) : (
              <ImageOutlinedIcon
                sx={{
                  fontSize: isMobile ? 24 : 28,
                  color: "#d4d4d8",
                }}
              />
            )}
          </div>
        ))}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: isMobile ? 15 : 16, color: "#18181b", marginBottom: 4, wordBreak: "break-word" }}>
          {ad.head || "Новое объявление"}
        </div>
        {ad.text && (
          <div
            style={{
              fontSize: isMobile ? 12 : 13,
              color: "#71717a",
              marginBottom: 6,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {ad.text}
          </div>
        )}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: isMobile ? 11 : 12, color: "#a1a1aa" }}>
          <span>{ad.banners?.length || 0} баннер(ов)</span>
          <span>·</span>
          <span>{new Date(ad.created_at).toLocaleDateString("ru-RU")}</span>
        </div>
        {ad.moderate_status === "REJECTED" && (
          <div style={{ marginTop: 8, fontSize: isMobile ? 11 : 12, color: "#dc2626", lineHeight: 1.4 }}>
            {ad.reject_reason || "Модерация отклонена. Свяжитесь со службой поддержки."}
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "row" : "column",
          alignItems: isMobile ? "center" : "flex-end",
          justifyContent: isMobile ? "space-between" : "flex-start",
          gap: 10,
          flexShrink: 0,
        }}
      >
        <span
          style={{
            padding: "4px 10px",
            borderRadius: 20,
            fontSize: isMobile ? 11 : 12,
            fontWeight: 600,
            color: status.color,
            background: status.bg,
            whiteSpace: "nowrap",
          }}
        >
          {status.label}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }} onClick={(e) => e.stopPropagation()}>
          <Switch
            size={isMobile ? "small" : "medium"}
            checked={ad.status === "ACTIVE"}
            onChange={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              const confirm = window.confirm(
                `Вы действительно хотите ${e.target.checked ? "включить" : "выключить"} объявление?`
              );
              if (!confirm) return;
              await onToggleStatus(ad.id, e.target.checked);
            }}
          />
          <button
            type="button"
            onClick={() => onEdit(ad.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: isMobile ? "6px 10px" : "8px 14px",
              borderRadius: 6,
              border: "none",
              background: "#DFE8FF",
              color: "#3F73F9",
              fontSize: isMobile ? 12 : 13,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
            Редактировать
          </button>
        </div>
      </div>
    </div>
  );
};

export const Ads = ({
  campaignId,
  openAd,
  currentAdId: _currentAdId,
  upd,
  setUpd,
  updBanner,
  setUpdBanner,
  setHasUnsavedChanges,
  hasUnsavedChanges = false,
  saveFormRef,
  isMobile = false,
  changeTab,
  disable = false,
  mediaPlanExpired = false,
  showNextButton = false,
}) => {
  const { user } = useUser();
  const [ads, setAds] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [modalAdId, setModalAdId] = useState(null)
  const [modalMode, setModalMode] = useState("edit")
  const [isCreating, setIsCreating] = useState(false)
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const fetchData = async () => {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append(
        "Authorization",
        "Bearer " + localStorage.getItem("token")
      )
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/fetch?campaign_id=${campaignId}`, {
        method: "GET",
        headers: myHeaders,
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const adsOnly = list.filter((item) => item && item.type !== "feed");
        const sortedAds = [...adsOnly].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setAds(sortedAds);
      }
    }
    fetchData()
  }, [upd, updBanner, campaignId])

  const createNewAd = async () => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("token")
    )
    const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/create?campaign_id=${campaignId}`, {
      method: "POST",
      body: JSON.stringify({}),
      headers: myHeaders,
    });
    if (response.ok) {
      const data = await response.json();
      trackWizardEvent("wizard_tab_ads", campaignId);
      return data
    }
  }

  const openModal = async (adId, mode = "edit") => {
    if (hasUnsavedChanges && modalOpen && modalAdId && modalAdId !== adId) {
      const confirmSave = window.confirm("Вы не сохранили объявление, сохранить?");
      if (confirmSave) {
        await saveFormRef?.current?.(modalAdId);
      }
    }
    setModalMode(mode);
    setModalAdId(adId);
    setModalOpen(true);
    openAd(adId);
  };

  const modalTitle =
    modalMode === "create"
      ? "Создание объявления"
      : (() => {
          const ad = ads.find((item) => item.id === modalAdId);
          return ad?.head ? `Редактирование объявления — ${ad.head}` : "Редактирование объявления";
        })();

  const closeModal = async (force = false) => {
    if (!force && hasUnsavedChanges) {
      const confirmSave = window.confirm("Вы не сохранили объявление, сохранить?");
      if (confirmSave) {
        await saveFormRef?.current?.(modalAdId);
      }
    }
    setModalOpen(false);
    setModalAdId(null);
    setHasUnsavedChanges?.(false);
    openAd(null, false, true);
  };

  const handleCreateAd = async () => {
    const confirm = window.confirm("Вы действительно хотите создать новое объявление?");
    if (!confirm) return;
    setIsCreating(true);
    try {
      const ad = await createNewAd();
      if (ad?.id) {
        setUpd((value) => !value);
        await openModal(ad.id, "create");
      }
    } finally {
      setIsCreating(false);
    }
  };

  const updateStatus = async (id, status) => {
    if (status) {
      const enableLock = getAdEditLockState({
        mediaPlanExpired,
        status: "STOPPED",
        hasImpressions: false,
      });
      if (enableLock.locked) {
        enqueueSnackbar(enableLock.message, { variant: "info" });
        return;
      }
    }

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("token")
    )
    try {
      const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/update?_id=${id}`, {
        method: "POST",
        body: JSON.stringify({
          status: status ? 'ACTIVE' : 'STOPPED'
        }),
        headers: myHeaders,
      });
      if (response.ok) {
        setUpd((value) => !value);
        if (status) {
          trackWizardEvent("ads_enabled", campaignId);
        }
        enqueueSnackbar(`Объявление успешно ${status ? 'включено' : 'выключено'}`, { variant: "success" });
      } else {
        let errorMessage = "Ошибка при изменении статуса";
        try {
          const errorData = await response.json();
          if (errorData?.detail) errorMessage = String(errorData.detail);
        } catch (_) {}
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    } catch (error) {
      enqueueSnackbar("Ошибка сети при изменении статуса", { variant: "error" });
    }
  }

  const hasAds = ads.length > 0;
  const listSubtitle = hasAds
    ? `Всего объявлений: ${ads.length}`
    : "Создайте первое объявление для кампании";

  return (
    <div
      style={{
        width: "100%",
        padding: isMobile ? "16px" : "20px 24px 32px",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, color: "#18181b" }}>Объявления</h2>
          <p style={{ margin: "6px 0 0", fontSize: isMobile ? 12 : 13, color: "#71717a" }}>
            {listSubtitle}
          </p>
        </div>
        <button
          type="button"
          disabled={isCreating}
          onClick={handleCreateAd}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: isMobile ? "10px 16px" : "10px 20px",
            borderRadius: 8,
            border: "none",
            background: isCreating ? "#a5b4fc" : "#3F73F9",
            color: "#fff",
            fontSize: isMobile ? 14 : 15,
            fontWeight: 600,
            cursor: isCreating ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <AddIcon sx={{ fontSize: 20 }} />
          {isCreating ? "Создание…" : "Создать объявление"}
        </button>
      </div>

      {!hasAds ? (
        <div
          style={{
            marginTop: 16,
            padding: isMobile ? "32px 20px" : "48px 32px",
            borderRadius: 12,
            border: "2px dashed #e4e4e7",
            background: "#fafafa",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 600, color: "#52525b", marginBottom: 8 }}>
            Объявлений пока нет
          </div>
          <div style={{ fontSize: isMobile ? 13 : 14, color: "#a1a1aa", marginBottom: 20 }}>
            Нажмите «Создать объявление», чтобы добавить баннеры и тексты
          </div>
          <button
            type="button"
            disabled={isCreating}
            onClick={handleCreateAd}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 20px",
              borderRadius: 8,
              border: "none",
              background: "#3F73F9",
              color: "#fff",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
            Создать объявление
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
          {ads.map((ad) => (
            <AdListItem
              key={ad.id}
              ad={ad}
              isMobile={isMobile}
              onEdit={openModal}
              onToggleStatus={updateStatus}
            />
          ))}
        </div>
      )}

      {showNextButton && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <LoadingButton
            loadingPosition="end"
            variant="contained"
            disabled={disable}
            onClick={() => {
              trackWizardEvent("wizard_tab_ads", campaignId);
              if (!user?.is_trial || user.is_trial === false) {
                changeTab(null, "4");
              } else {
                changeTab(null, "5");
              }
            }}
            endIcon={<SendIcon />}
          >
            Далее
          </LoadingButton>
        </Box>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => closeModal(false)}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            pr: 6,
            pb: 1,
            pt: isMobile ? 2 : 2.5,
            pl: isMobile ? 2 : 3,
            fontSize: isMobile ? 17 : 20,
            fontWeight: 600,
            color: "#18181b",
          }}
        >
          {modalTitle}
          <IconButton
            onClick={() => closeModal(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            aria-label="Закрыть"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 1 }}>
          {modalAdId && (
            <Banners
              campaignId={campaignId}
              currentAdId={modalAdId}
              changeTab={changeTab}
              disable={disable}
              mediaPlanExpired={mediaPlanExpired}
              upd={upd}
              setUpd={setUpd}
              updBanner={updBanner}
              setUpdBanner={setUpdBanner}
              setHasUnsavedChanges={setHasUnsavedChanges}
              saveFormRef={saveFormRef}
              isMobile={isMobile}
              hideNextButton
              inModal
              onClose={closeModal}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}






function formatYmlParentLabel(v) {
  if (v == null || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function YmlFeedPendingSection({ pending, loading, expanded, onToggleExpanded }) {
  if (loading) {
    return (
      <div className="ads-mgr-delta">
        <div className="ads-mgr-delta__head">Загрузка изменений прайс-листа…</div>
      </div>
    );
  }
  if (pending == null || (typeof pending === "object" && Object.keys(pending).length === 0)) {
    return null;
  }
  if (pending.kind === "new_feed") {
    return (
      <div className="ads-mgr-delta is-new">
        <div className="ads-mgr-delta__head">Новый прайс-лист</div>
        <div className="ads-mgr-delta__item">
          Загружен новый URL или первая выгрузка. Нужно проверить фид и принять решение.
        </div>
      </div>
    );
  }
  if (pending.kind === "delta" && Array.isArray(pending.items) && pending.items.length > 0) {
    const items = pending.items;
    const limit = 8;
    const shown = expanded ? items : items.slice(0, limit);
    const hasMore = items.length > limit;
    return (
      <div className="ads-mgr-delta">
        <div className="ads-mgr-delta__head">
          Что изменилось · {items.length}
        </div>
        <div className="ads-mgr-delta__body">
          {shown.map((item, idx) => {
            const ch = item.change ?? item.type;
            const yid = item.yml_category_id ?? item.category_id ?? "—";
            const nm = item.name ?? "";
            if (ch === "offers_count_changed") {
              const was = item.previous_offers_count ?? item.previous_count ?? item.was ?? "—";
              const now = item.current_offers_count ?? item.new_count ?? item.offers_count ?? item.now ?? "—";
              return (
                <div key={idx} className="ads-mgr-delta__item">
                  <div>
                    <span className="ads-mgr-delta__name">{nm || "Категория"}</span>
                    {" "}
                    <span className="ads-mgr-delta__id">id {yid}</span>
                  </div>
                  <div className="ads-mgr-delta__meta">
                    <span className="ads-mgr-delta__from">{was}</span>
                    {" → "}
                    <span className="ads-mgr-delta__to">{now}</span>
                    {" офферов"}
                  </div>
                </div>
              );
            }
            if (ch === "new_category") {
              const cnt = item.current_offers_count ?? item.offers_count ?? "—";
              return (
                <div key={idx} className="ads-mgr-delta__item">
                  <div>
                    <span className="ads-mgr-delta__name">{nm || "Новая категория"}</span>
                    {" "}
                    <span className="ads-mgr-delta__id">id {yid}</span>
                  </div>
                  <div className="ads-mgr-delta__meta">Добавлена · офферов: {cnt}</div>
                </div>
              );
            }
            if (ch === "name_changed") {
              const prev = item.previous_name ?? "";
              const cur = item.current_name ?? "";
              return (
                <div key={idx} className="ads-mgr-delta__item">
                  <div>
                    <span className="ads-mgr-delta__name">Переименование</span>
                    {" "}
                    <span className="ads-mgr-delta__id">id {yid}</span>
                  </div>
                  <div className="ads-mgr-delta__meta">
                    <span className="ads-mgr-delta__from">«{prev}»</span>
                    {" → "}
                    <span className="ads-mgr-delta__to">«{cur}»</span>
                  </div>
                </div>
              );
            }
            if (ch === "parent_changed") {
              const pp = formatYmlParentLabel(item.previous_parent);
              const cp = formatYmlParentLabel(item.current_parent);
              return (
                <div key={idx} className="ads-mgr-delta__item">
                  <div>
                    <span className="ads-mgr-delta__name">{nm || "Категория"}</span>
                    {" "}
                    <span className="ads-mgr-delta__id">id {yid}</span>
                  </div>
                  <div className="ads-mgr-delta__meta">
                    родитель {pp} → <span className="ads-mgr-delta__to">{cp}</span>
                  </div>
                </div>
              );
            }
            return (
              <div key={idx} className="ads-mgr-delta__item">
                {String(ch || "изменение")}: {JSON.stringify(item)}
              </div>
            );
          })}
        </div>
        {hasMore && (
          <button type="button" className="ads-mgr-delta__more" onClick={onToggleExpanded}>
            {expanded ? "Свернуть" : `Показать все (${items.length})`}
          </button>
        )}
      </div>
    );
  }
  return null;
}

export const AdsForManagers = ({ campaignId, isErid, setUpd, onDone, refreshKey, moderationFocus, wide }) => {
  const [ads, setAds] = useState([])
  const [adsLoading, setAdsLoading] = useState(true)
  const [feedData, setFeedData] = useState(null)
  const [feedPendingChanges, setFeedPendingChanges] = useState(null)
  const [feedPendingLoading, setFeedPendingLoading] = useState(false)
  const [deltaExpanded, setDeltaExpanded] = useState(false)
  const { user } = useUser();

  const showFeedBlock = Boolean(feedData && moderationFocus !== "ads")
  const showAdsBlock = moderationFocus !== "feed"

  const { enqueueSnackbar } = useSnackbar();

  const [eridByAd, setEridByAd] = useState({});
  const [eridVerifiedByAd, setEridVerifiedByAd] = useState({});
  const [rejectReasons, setRejectReasons] = useState({});

  const loadFeedPendingChanges = useCallback(async () => {
    if (moderationFocus === "ads") {
      setFeedPendingChanges(null);
      setFeedPendingLoading(false);
      return;
    }
    setFeedPendingLoading(true);
    try {
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append("Authorization", "Bearer " + localStorage.getItem("admin_token"));
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/campaigns/${campaignId}/yml/feed-pending-changes`,
        { method: "GET", headers: myHeaders }
      );
      if (res.ok) {
        const raw = await res.json();
        const pc =
          raw?.yml_feed_pending_changes ??
          raw?.data?.yml_feed_pending_changes ??
          (raw && typeof raw === "object" && "kind" in raw ? raw : null);
        setFeedPendingChanges(pc ?? null);
      } else {
        setFeedPendingChanges(null);
      }
    } catch {
      setFeedPendingChanges(null);
    } finally {
      setFeedPendingLoading(false);
    }
  }, [campaignId, moderationFocus]);

  useEffect(() => {
    setDeltaExpanded(false);
    void loadFeedPendingChanges();
  }, [campaignId, refreshKey, moderationFocus, loadFeedPendingChanges]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setAdsLoading(true);
      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");
      myHeaders.append(
        "Authorization",
        "Bearer " + localStorage.getItem("admin_token")
      )
      try {
        const response = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/fetch?campaign_id=${campaignId}`, {
          method: "GET",
          headers: myHeaders,
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        const feed = list.find((item) => item && item.type === "feed") || null;
        const adsOnly = list.filter((item) => item && item.type !== "feed");
        const sortedAds = [...adsOnly].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        const nextErid = {};
        const nextVerified = {};
        sortedAds.forEach((ad) => {
          const preset = existingErid(ad);
          nextErid[ad.id] = preset;
          if (preset) nextVerified[ad.id] = true;
        });
        if (cancelled) return;
        setFeedData(feed);
        setAds(sortedAds);
        setEridByAd(nextErid);
        setEridVerifiedByAd(nextVerified);
      } finally {
        if (!cancelled) setAdsLoading(false);
      }
    }
    fetchData()
    return () => {
      cancelled = true;
    };
  }, [campaignId, refreshKey])

  const rejectCampaign = async (advert_id, useCampaignId, rejectReason) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("admin_token")
    );
    const q = useCampaignId
      ? `campaign_id=${campaignId}`
      : `advert_id=${advert_id}`;
    const reasonQuery = rejectReason ? `&reject_reason=${encodeURIComponent(rejectReason)}` : "";
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/moderation/reject?${q}${reasonQuery}`, {
      method: "POST",
      headers: myHeaders,
    });
    if (res.ok) {
      const hybridFull = Boolean(feedData && ads.length > 0);
      const feedAccepted = feedData?.yml_feed_moderate_status === "ACCEPTED";
      const hasModeratingAds = ads.some(
        (a) => a.status === "ACTIVE" && a.moderate_status === "MODERATING"
      );
      if (moderationFocus === "ads") {
        if (useCampaignId) return;
        enqueueSnackbar(
          hybridFull
            ? "Объявление отклонено. Прайс‑лист при необходимости обработайте во вкладке «Товарка»."
            : "Объявление отклонено",
          { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else if (moderationFocus === "feed") {
        if (!useCampaignId) return;
        enqueueSnackbar(
          hybridFull && hasModeratingAds
            ? "Прайс‑лист отклонён. Объявления при необходимости обработайте во вкладке «Баннеры»."
            : "Фид-кампания отклонена",
          { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else {
        if (useCampaignId) {
          enqueueSnackbar(
            hybridFull && hasModeratingAds
              ? "Прайс‑лист отклонён. При необходимости обработайте объявления."
              : "Фид-кампания отклонена",
            { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        } else {
          enqueueSnackbar(
            hybridFull
              ? "Объявление отклонено. РК типа «объявления + фид» может требовать решения по прайс‑листу."
              : "Объявление отклонено",
            { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        }
        if (setUpd) setUpd((v) => !v);
        if (onDone) {
          if (useCampaignId) {
            if (!hybridFull || !hasModeratingAds) onDone();
          } else if (!hybridFull || feedAccepted) onDone();
        }
      }
      if (useCampaignId) void loadFeedPendingChanges();
      return;
    }
    let errorMessage = "Не удалось отклонить объявление";
    try {
      const errorData = await res.json();
      if (errorData?.detail) errorMessage = String(errorData.detail);
    } catch (_) {
      errorMessage = "Не удалось отклонить объявление";
    }
    enqueueSnackbar(errorMessage, {
      variant: "error",
      autoHideDuration: 3000,
      anchorOrigin: { vertical: "top", horizontal: "right" },
    });
  }

  const aproveCampaign = async (advert_id, useCampaignId) => {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append(
      "Authorization",
      "Bearer " + localStorage.getItem("admin_token")
    );
    const q = useCampaignId
      ? `campaign_id=${campaignId}`
      : `advert_id=${advert_id}`;
    const res = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/moderation/approve?${q}`, {
      method: "POST",
      headers: myHeaders,
    });
    if (res.ok) {
      const hybridFull = Boolean(feedData && ads.length > 0);
      const feedAccepted = feedData?.yml_feed_moderate_status === "ACCEPTED";
      const hasModeratingAds = ads.some(
        (a) => a.status === "ACTIVE" && a.moderate_status === "MODERATING"
      );
      if (moderationFocus === "ads") {
        if (useCampaignId) return;
        enqueueSnackbar(
          hybridFull
            ? "Объявление одобрено. Прайс‑лист примите во вкладке «Товарка»."
            : "Объявление одобрено.",
          { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else if (moderationFocus === "feed") {
        if (!useCampaignId) return;
        enqueueSnackbar(
          hybridFull && hasModeratingAds
            ? "Прайс‑лист одобрен. Объявления примите во вкладке «Баннеры»."
            : "Фид-кампания одобрена",
          { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
        );
        if (setUpd) setUpd((v) => !v);
        if (onDone) onDone();
      } else {
        if (useCampaignId) {
          enqueueSnackbar(
            hybridFull && hasModeratingAds
              ? "Прайс‑лист одобрен. Кампания остаётся на модерации, пока не приняты все объявления."
              : "Фид-кампания одобрена",
            { variant: "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        } else {
          enqueueSnackbar(
            hybridFull
              ? "Объявление одобрено. Кампания остаётся на модерации, пока не будет принято решение по прайс‑листу (YML)."
              : "Объявление одобрено.",
            { variant: hybridFull ? "info" : "success", autoHideDuration: 4000, anchorOrigin: { vertical: "top", horizontal: "right" } }
          );
        }
        if (setUpd) setUpd((v) => !v);
        if (onDone) {
          if (useCampaignId) {
            if (!hybridFull || !hasModeratingAds) onDone();
          } else if (!hybridFull || feedAccepted) onDone();
        }
      }
      if (useCampaignId) void loadFeedPendingChanges();
    }
  }


  return (
    <div className="ads-for-managers" style={{ width: "100%", boxSizing: "border-box" }}>
      <div className="ads-mgr-stack">
          {moderationFocus !== "ads" && !adsLoading && !showFeedBlock && (
            <YmlFeedPendingSection
              pending={feedPendingChanges}
              loading={feedPendingLoading}
              expanded={deltaExpanded}
              onToggleExpanded={() => setDeltaExpanded((v) => !v)}
            />
          )}
          {adsLoading && (
            <AdsManagersLoading
              label={moderationFocus === "feed" ? "Загрузка прайс-листа…" : "Загрузка объявлений…"}
            />
          )}
          {!adsLoading && moderationFocus === "feed" && !feedData && (
            <div className="ads-mgr-empty">Прайс‑лист (YML) в ответе не найден.</div>
          )}
          {!adsLoading && showFeedBlock && (() => {
            const feedStatus = getFeedStatusMeta(feedData);
            return (
            <div className="ads-mgr-card ads-mgr-feed" style={{ borderLeftColor: feedStatus.color }}>
              <div className="ads-mgr-card__head">
                <div style={{ minWidth: 0 }}>
                  <p className="ads-mgr-card__title">Прайс-лист</p>
                  <p className="ads-mgr-card__text">YML-фид кампании</p>
                </div>
                <span
                  className="ads-mgr-status"
                  style={{
                    color: feedStatus.color,
                    background: feedStatus.bg,
                    border: `1px solid ${feedStatus.border}`,
                  }}
                >
                  {feedStatus.label}
                </span>
              </div>
              <YmlFeedPendingSection
                pending={feedPendingChanges}
                loading={feedPendingLoading}
                expanded={deltaExpanded}
                onToggleExpanded={() => setDeltaExpanded((v) => !v)}
              />
              <FeedUrlRow
                label="Исходный фид"
                url={feedData.yml_original_feed_url}
                onCopied={() => enqueueSnackbar("Ссылка исходного фида скопирована", { variant: "success" })}
              />
              {feedData.yml_original_feed_url && <FeedRejectNotice feed={feedData} />}
              <FeedUrlRow
                label="Реферальный фид"
                url={feedData.yml_referral_feed_url}
                onCopied={() => enqueueSnackbar("Ссылка реферального фида скопирована", { variant: "success" })}
              />
              {!feedData.yml_original_feed_url && <FeedRejectNotice feed={feedData} />}
              {isErid && (
                <div className="ads-mgr-actions">
                  <button
                    type="button"
                    className="ads-mgr-btn is-ghost"
                    onClick={() => rejectCampaign(null, true)}
                  >
                    Отклонить
                  </button>
                  <button
                    type="button"
                    className="ads-mgr-btn is-approve"
                    onClick={() => aproveCampaign(null, true)}
                  >
                    Одобрить
                  </button>
                </div>
              )}
            </div>
            );
          })()}
          {!adsLoading && showAdsBlock && !feedData && ads.length === 0 && (
            <div className="ads-mgr-empty">Нет созданных объявлений</div>
          )}
          {!adsLoading && !moderationFocus && feedData && ads.length === 0 && (
            <div className="ads-mgr-empty">Объявлений нет — модерация только по прайс‑листу.</div>
          )}
          {!adsLoading && showAdsBlock &&
            [...ads]
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .map((ad) => {
              const status = getAdStatusMeta(ad);
              const presetErid = existingErid(ad);
              const banners = [...(ad.banners || [])].sort(
                (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
              );
              return (
              <div
                key={ad.id}
                className="ads-mgr-card"
                style={{ borderLeftColor: status.color }}
              >
                <div className="ads-mgr-card__head">
                  <div style={{ minWidth: 0 }}>
                    <p className="ads-mgr-card__title">{ad.head || "Новое объявление"}</p>
                    {ad.text ? <p className="ads-mgr-card__text">{ad.text}</p> : null}
                  </div>
                  <span
                    className="ads-mgr-status"
                    style={{ color: status.color, background: status.bg, border: `1px solid ${status.border}` }}
                  >
                    {status.label}
                  </span>
                </div>
                <div className={`ads-mgr-card__split${wide ? " is-wide" : ""}`}>
                  {banners.length > 0 && (
                    <div className="ads-mgr-banners">
                      {banners.map((el, index) => (
                        <ManagerBannerThumb
                          key={el.id || index}
                          banner={el}
                          alt={ad.head || "Баннер"}
                        />
                      ))}
                    </div>
                  )}
                  <div className="ads-mgr-meta">
                    <dl className="ads-mgr-dl">
                      <dt>Создано</dt>
                      <dd>
                        {new Date(ad.created_at).toLocaleString("ru-RU", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </dd>
                      <dt>ERID</dt>
                      <dd>
                        <span
                          className={`ads-mgr-erid${presetErid ? "" : " is-empty"}`}
                          role={presetErid ? "button" : undefined}
                          tabIndex={presetErid ? 0 : undefined}
                          onClick={() => {
                            if (!presetErid) return;
                            navigator.clipboard.writeText(presetErid);
                            enqueueSnackbar("ERID скопирован в буфер обмена", { variant: "success" });
                          }}
                          onKeyDown={(e) => {
                            if (!presetErid) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigator.clipboard.writeText(presetErid);
                              enqueueSnackbar("ERID скопирован в буфер обмена", { variant: "success" });
                            }
                          }}
                        >
                          {presetErid || "Не указан"}
                        </span>
                      </dd>
                      {ad.moderate_status === "REJECTED" && (
                        <>
                          <dt>Причина</dt>
                          <dd>{ad.reject_reason || "Не указана"}</dd>
                        </>
                      )}
                      {ad.landing_page && (
                        <>
                          <dt>Посадочная</dt>
                          <dd>
                            <a
                              href={ad.landing_page}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#3F73F9", fontWeight: 500 }}
                            >
                              {ad.landing_page}
                            </a>
                          </dd>
                        </>
                      )}
                    </dl>
                    {ad.landing_page && (
                      <div className="ads-mgr-field">
                        <label>Ссылка для внешних систем</label>
                        <div
                          className="ads-mgr-extlink"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const firstBanner = ad.banners && ad.banners.length > 0 ? ad.banners[0] : null;
                            const bannerId = firstBanner ? firstBanner.id : "";
                            let utmMarkers = ad.utm_markers || getDefaultUtmSource(ad.signup_domain, user?.id);
                            utmMarkers = utmMarkers
                              .replace(/\{CAMPAIGN_ID\}/g, campaignId)
                              .replace(/\{BANNER_ID\}/g, bannerId)
                              .replace(/&utm_term=\{SITE_ID\}/g, "")
                              .replace(/\/\{SITE_ID\}/g, "");
                            const separator = ad.landing_page && ad.landing_page.includes("?") ? "&" : "?";
                            const fullLink = `${ad.landing_page}${separator}${utmMarkers}`;
                            navigator.clipboard.writeText(fullLink);
                            enqueueSnackbar("Ссылка скопирована в буфер обмена", {
                              variant: "success",
                              autoHideDuration: 3000,
                              anchorOrigin: { vertical: "top", horizontal: "right" },
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
                          }}
                          title="Нажмите, чтобы скопировать ссылку"
                        >
                          {`${ad.landing_page}${ad.landing_page && ad.landing_page.includes("?") ? "&" : "?"}`}
                          {(ad.utm_markers || getDefaultUtmSource(ad.signup_domain, user?.id))
                            .replace(/\{CAMPAIGN_ID\}/g, campaignId)
                            .replace(/\{BANNER_ID\}/g, ad.banners && ad.banners.length > 0 ? ad.banners[0].id : "")
                            .replace(/&utm_term=\{SITE_ID\}/g, "")
                            .replace(/\/\{SITE_ID\}/g, "")}
                        </div>
                        <p style={{ fontSize: 12, marginTop: 5, fontStyle: "italic", color: "#71717a" }}>
                          Не забудьте заменить текст {`{PRG}`}
                        </p>
                      </div>
                    )}
                    {isErid && (
                      <div>
                        <div className="ads-mgr-field-row">
                          <div className="ads-mgr-field" style={{ flex: 1, minWidth: 0 }}>
                            <label htmlFor={`erid-${ad.id}`}>Укажите erid из ОРД</label>
                            <input
                              id={`erid-${ad.id}`}
                              key={`erid-${ad.id}-${presetErid}`}
                              className="ads-mgr-input"
                              placeholder="Идентификатор креатива…"
                              type="text"
                              name={`erid-${ad.id}`}
                              autoComplete="off"
                              spellCheck={false}
                              value={eridByAd[ad.id] ?? presetErid}
                              onChange={(e) => {
                                const value = e.target.value;
                                setEridByAd((prev) => ({ ...prev, [ad.id]: value }));
                                setEridVerifiedByAd((prev) => ({
                                  ...prev,
                                  [ad.id]: Boolean(presetErid) && value === presetErid,
                                }));
                              }}
                            />
                          </div>
                          <button
                            type="button"
                            className="ads-mgr-btn is-ghost"
                            onClick={() => {
                              const fetchApi = async () => {
                                const currentErid = (eridByAd[ad.id] ?? presetErid).trim();
                                const myHeaders = new Headers();
                                myHeaders.append("Content-Type", "application/json");
                                myHeaders.append(
                                  "Authorization",
                                  "Bearer " + localStorage.getItem("admin_token")
                                );
                                const res = await fetch(`${DOMAIN_API_HOST}/api/v1/advertisements/moderation/erid?advert_id=${ad.id}&erid=${encodeURIComponent(currentErid)}`, {
                                  method: "POST",
                                  headers: myHeaders
                                });
                                if (res.ok) {
                                  setEridVerifiedByAd((prev) => ({ ...prev, [ad.id]: true }));
                                  enqueueSnackbar("Креатив с erid - " + currentErid + " успешно найден", {
                                    variant: "success",
                                    autoHideDuration: 3000,
                                    anchorOrigin: { vertical: "top", horizontal: "right" },
                                  });
                                } else {
                                  setEridVerifiedByAd((prev) => ({ ...prev, [ad.id]: false }));
                                  enqueueSnackbar("Произошла проблема, проверьте корректность erid или инн клиента.", {
                                    variant: "error",
                                    autoHideDuration: 3000,
                                    anchorOrigin: { vertical: "top", horizontal: "right" },
                                  });
                                }
                              };
                              fetchApi();
                            }}
                          >
                            Проверить
                          </button>
                        </div>
                        <div className="ads-mgr-field" style={{ marginTop: 10 }}>
                          <label htmlFor={`reject-${ad.id}`}>Причина отклонения</label>
                          <select
                            id={`reject-${ad.id}`}
                            className="ads-mgr-select"
                            value={rejectReasons[ad.id] || ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setRejectReasons((prev) => ({
                                ...prev,
                                [ad.id]: value,
                              }));
                            }}
                          >
                            <option value="" disabled>Выберите причину</option>
                            {REJECT_REASONS.map((reason) => (
                              <option key={reason} value={reason}>{reason}</option>
                            ))}
                          </select>
                        </div>
                        <div className="ads-mgr-actions">
                          <button
                            type="button"
                            className="ads-mgr-btn is-ghost"
                            onClick={() => {
                              const selectedReason = rejectReasons[ad.id];
                              if (!selectedReason) {
                                enqueueSnackbar("Выберите причину отклонения", {
                                  variant: "info",
                                  autoHideDuration: 3000,
                                  anchorOrigin: { vertical: "top", horizontal: "right" },
                                });
                                return;
                              }
                              rejectCampaign(ad.id, false, selectedReason)
                            }}
                          >
                            Отклонить
                          </button>
                          <button
                            type="button"
                            className="ads-mgr-btn is-approve"
                            onClick={() => {
                              const currentErid = (eridByAd[ad.id] ?? presetErid).trim();
                              const canApproveThis = Boolean(
                                eridVerifiedByAd[ad.id] || (presetErid && currentErid === presetErid)
                              );
                              if (!canApproveThis) {
                                enqueueSnackbar("Обязательно укажите erid и проверьте его", {
                                  variant: "info",
                                  autoHideDuration: 3000,
                                  anchorOrigin: { vertical: "top", horizontal: "right" },
                                });
                                return;
                              }
                              aproveCampaign(ad.id)
                            }}
                          >
                            Одобрить
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
            })}
      </div>
    </div>
  )
}